import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useState } from 'react'
import { MediaAsset } from '../../views'
import { useImageCacheWriter } from '../images'
import {
	quickImageGenerateCallbackAtom,
	quickImageUploadCallbackAtom,
	storyAdImageHistoryAtom,
	storyAdImageIdAtom,
	storyAdMetaBackdropOverlayProModeAtom,
	storyAdMetaBackdropTransformAtom,
	storyAdMetaOverlayAtom,
} from '../state'
import { trackImageEdited, trackImageUploaded } from '../tracking'
import { ImageFilmstrip } from './image-filmstrip'
import { OverlayEditor } from './shared'

export function StoryAdImageWidget() {
	const onQuickUpload = useAtomValue(quickImageUploadCallbackAtom)
	const onQuickGenerate = useAtomValue(quickImageGenerateCallbackAtom)
	const assets = useAtomValue(storyAdImageHistoryAtom)
	const { cacheImageUrl } = useImageCacheWriter()

	const [storyAdImageId, setStoryAdImageId] = useAtom(storyAdImageIdAtom)
	const setBackdropTransform = useSetAtom(storyAdMetaBackdropTransformAtom)

	const handleImageSelect = useCallback((asset: MediaAsset) => {
		setStoryAdImageId(asset.id)
		cacheImageUrl(asset.id, asset.url)
		// Reset backdrop transform based on collection
		setBackdropTransform((prev) => ({
			...prev,
			scale: 1.2,
		}))
	}, [setStoryAdImageId, cacheImageUrl, setBackdropTransform])

	const [isQuickUploading, setIsQuickUploading] = useState(false)
	const handleQuickUpload = useCallback((file: File) => {
		if (!onQuickUpload) { return }
		setIsQuickUploading(true)
		onQuickUpload(
			file,
			(uuid, url) => {
				setStoryAdImageId(uuid)
				cacheImageUrl(uuid, url)
				// User-uploaded images are not in 'generated' collection, so use 1x scale
				setBackdropTransform((prev) => ({
					...prev,
					scale: 1,
				}))
				trackImageUploaded('story')
				setIsQuickUploading(false)
			},
			() => {
				// Reset upload state on error so user can try again
				setIsQuickUploading(false)
			},
		)
	}, [onQuickUpload, setStoryAdImageId, cacheImageUrl, setBackdropTransform])
	const handleQuickGenerate = useCallback((
		prompt: string,
		onComplete: () => void,
		onError?: (message: string) => void,
	) => {
		if (!onQuickGenerate) { return }
		onQuickGenerate(prompt, (uuid) => {
			setStoryAdImageId(uuid)
			// Generated images should have 1.2x scale
			setBackdropTransform((prev) => ({
				...prev,
				scale: 1.2,
			}))
			onComplete()
		}, onError)
	}, [onQuickGenerate, setStoryAdImageId, setBackdropTransform])

	const [storyAdMetaOverlay, setStoryAdMetaOverlay] = useAtom(storyAdMetaOverlayAtom)
	const [storyAdMetaBackdropOverlayProMode, setStoryAdMetaBackdropOverlayProMode] = useAtom(
		storyAdMetaBackdropOverlayProModeAtom,
	)

	if (!storyAdMetaOverlay) {
		return null
	}

	return (
		<>
			<ImageFilmstrip
				assets={assets}
				onSelect={handleImageSelect}
				selectedAsset={storyAdImageId ?? ''}
				onQuickUpload={handleQuickUpload}
				onQuickGenerate={handleQuickGenerate}
				isQuickUploading={isQuickUploading}
				onImageAction={(action) => trackImageEdited(action, 'story')}
			/>
			<OverlayEditor
				overlay={storyAdMetaOverlay}
				onChange={setStoryAdMetaOverlay}
				proMode={storyAdMetaBackdropOverlayProMode ?? false}
				onProModeChange={setStoryAdMetaBackdropOverlayProMode}
				simulatorType="story"
			/>
		</>
	)
}
